// lodash から isEmpty 関数をインポート
import { isEmpty } from 'lodash';
// notistack5 から useSnackbar フックをインポート
import { useSnackbar } from 'notistack5';
// react から useState フックをインポート
import { useState } from 'react';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
// QuestionCompliaEnquete 型をインポート
import { QuestionCompliaEnquete } from 'src/@types/compliaEnquete';
// GeneralEnquete 型をインポート
import { GeneralEnquete } from 'src/@types/generalEnquete';
// OverviewEnquete 型をインポート
import { OverviewEnquete } from 'src/@types/overviewEnquete';
// SeriesAnswerEnquete 型をインポート
import { SeriesAnswerEnquete } from 'src/@types/seriesEnquete';
// useUserApi フックをインポート
import { useUserApi } from 'src/api/useUserApi';
// ANSWER_STATUS 定数と AnswerStatus 型をインポート
import { ANSWER_STATUS, AnswerStatus } from 'src/constants';
// useEnqueteAnswer フックをインポート
import useEnqueteAnswer from 'src/features/general/enquete-answer/hooks/useEnqueteAnswer';
// useEnqueteAnswerGrid フックをインポート
import { useEnqueteAnswerGrid } from 'src/features/general/enquete-answer/hooks/useEnqueteAnswerGrid';
// EnqueteAnswerCompliaEntity などの型をインポート
import { EnqueteAnswerCompliaEntity, EnqueteAnswerEntity, FileType } from 'src/features/general/enquete-answer/store/enqueteAnswerSlice';
// MAX_DATA_SIZE 定数をインポート
import { MAX_DATA_SIZE } from 'src/generalConstants';
// useAuth フックをインポート
import useAuth from 'src/hooks/useAuth';
// useAppSelector フックをインポート
import { useAppSelector } from 'src/redux/store';

// 回答者ターゲット ID を受け取る useEnqueteAnswerSave フックを定義
const useEnqueteAnswerSave = (respondentTargetId: string) => {
  // 翻訳機能を使用するためのフックを呼び出し
  const { t } = useTranslation();
  // 認証情報からユーザー情報を取得
  const { user } = useAuth();
  // スナックバー通知を使用するためのフックを呼び出し
  const { enqueueSnackbar } = useSnackbar();

  // ユーザー API フックを使用
  const { getByEmail } = useUserApi();
  // アンケート回答グリッド関連の関数を取得
  const {
    saveAnswerComplia,
    tempSaveAnswerComplia,
    updateAnswerComplia,
    tempUpdateAnswerComplia,
    saveAnswer,
    tempSaveAnswer,
    updateAnswer,
    tempUpdateAnswer,
  } = useEnqueteAnswerGrid();
  // 確認履歴を取得するための関数を取得
  const { fetchConfirmHistories } = useEnqueteAnswer();

  // Redux ストアからアンケート回答データを取得
  const enqueteAnswerData = useAppSelector((state) => state.enqueteAnswer.enqueteAnswerData);
  // Redux ストアからアンケート回答が開いているかどうかを取得
  const isEnqueteAnswerOpen = useAppSelector((state) => state.enqueteAnswer.isEnqueteAnswerOpen);

  // 保存中かどうかを示す状態と更新関数を定義
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // 回答ステータスを取得、データが空でなければ status を使用
  const answerStatus = !isEmpty(enqueteAnswerData) ? enqueteAnswerData.status : undefined;
  // すでに回答済みかどうか、またはアンケートが開いていないかを判定
  const isAnswered = [ANSWER_STATUS.ANSWERED].some((v) => v === answerStatus) || !isEnqueteAnswerOpen;

  // 一般的な保存処理を行う非同期関数を定義
  const commonSaveHandler = async (tabData: GeneralEnquete | SeriesAnswerEnquete | OverviewEnquete, answerStatus: AnswerStatus) => {
    // 保存中状態を true に設定
    setIsSaving(true);

    // ユーザー ID を設定、ユーザーが存在すれば email を使用
    const userId = null !== user ? user.email : '';
    // ユーザーメールを設定、ユーザーが存在すれば email を使用
    const userEmail = null !== user ? user.email : '';

    // メールアドレスでユーザー情報を取得
    const myInfo = await getByEmail(userEmail);
    // ユーザー情報が見つからない場合
    if (!myInfo || myInfo.length === 0) {
      // 保存中状態を false に設定
      setIsSaving(false);
      // ユーザーデータが見つからないエラーをスロー
      throw Error(t('message.exceptionNotFoundUserData'));
    }

    // 保存用のパラメータオブジェクトを作成
    const params: EnqueteAnswerEntity = {
      userId: userId,
      userName: myInfo[0].fullNameJpn,
      userNameEng: myInfo[0].fullNameEng,
      status: answerStatus,
      respondentTargetId: respondentTargetId ?? '',
      answerData: { answerInputData: { tabData: [tabData] } },
      isDeleted: false,
    };

    try {
      // 回答データが最大サイズを超えるかチェック
      if (JSON.stringify(tabData).length > MAX_DATA_SIZE.ANSWER) {
        // データサイズ超過のエラー通知を表示
        enqueueSnackbar(t('message.exceptionDataSizeOver', { target: t('common.answer') }), { variant: 'error' });
      } else {
        // 既存の回答データがあり ID が存在する場合
        if (!isEmpty(enqueteAnswerData) && enqueteAnswerData.id) {
          // 既存の回答 ID を取得
          const answerId = enqueteAnswerData.id;
          // 一時保存か更新かを判定して実行
          answerStatus === ANSWER_STATUS.SAVED ? await tempUpdateAnswer(params, answerId) : await updateAnswer(params, answerId);
          // 確認履歴を取得
          await fetchConfirmHistories(answerId);
        } else {
          // 新規保存か一時保存かを判定して実行
          answerStatus === ANSWER_STATUS.SAVED ? await tempSaveAnswer(params) : await saveAnswer(params);
        }
      }
    } finally {
      // 最終的に保存中状態を false に設定
      setIsSaving(false);
    }
  };

  // Complia アンケートの保存処理を行う非同期関数を定義
  const compliaSaveHandler = async (
    tabData: QuestionCompliaEnquete,
    answerStatus: AnswerStatus,
    files: FileType[],
    deletedFiles: FileType[],
    surveyDetailId: string,
  ) => {
    // 保存中状態を true に設定
    setIsSaving(true);

    // ユーザー ID を設定、ユーザーが存在すれば email を使用
    const userId = null !== user ? user.email : '';
    // ユーザーメールを設定、ユーザーが存在すれば email を使用
    const userEmail = null !== user ? user.email : '';

    // メールアドレスでユーザー情報を取得
    const myInfo = await getByEmail(userEmail);
    // ユーザー情報が見つからない場合
    if (!myInfo || myInfo.length === 0) {
      // 保存中状態を false に設定
      setIsSaving(false);
      // ユーザーデータが見つからないエラーをスロー
      throw Error(t('message.exceptionNotFoundUserData'));
    }

    // 保存用のパラメータオブジェクトを作成
    const params: EnqueteAnswerCompliaEntity = {
      userId: userId,
      userName: myInfo[0].fullNameJpn,
      userNameEng: myInfo[0].fullNameEng,
      status: answerStatus,
      respondentTargetId: respondentTargetId ?? '',
      answerData: { answerInputData: { tabData: [tabData] } },
      isDeleted: false,
      surveyDetailId: surveyDetailId,
    };

    try {
      // 回答データが最大サイズを超えるかチェック
      if (JSON.stringify(tabData).length > MAX_DATA_SIZE.ANSWER) {
        // データサイズ超過のエラー通知を表示
        enqueueSnackbar(t('message.exceptionDataSizeOver', { target: t('common.answer') }), { variant: 'error' });
      } else {
        // 既存の回答データがあり ID が存在する場合
        if (!isEmpty(enqueteAnswerData) && enqueteAnswerData.id) {
          // 既存の回答 ID を取得
          const answerId = enqueteAnswerData.id;
          // 一時更新か更新かを判定して実行、ファイル情報も渡す
          answerStatus === ANSWER_STATUS.SAVED
            ? await tempUpdateAnswerComplia(params, answerId, files, deletedFiles)
            : await updateAnswerComplia(params, answerId, files, deletedFiles);
          // 確認履歴を取得
          await fetchConfirmHistories(answerId);
        } else {
          // 新規保存か一時保存かを判定して実行、ファイル情報も渡す
          answerStatus === ANSWER_STATUS.SAVED ? await tempSaveAnswerComplia(params, files) : await saveAnswerComplia(params, files);
        }
      }
    } finally {
      // 最終的に保存中状態を false に設定
      setIsSaving(false);
    }
  };

  // 状態とハンドラー関数をオブジェクトとして返す
  return {
    isSaving,
    isAnswered,
    commonSaveHandler,
    compliaSaveHandler,
  };
};

// useEnqueteAnswerSave フックをデフォルトエクスポート
export default useEnqueteAnswerSave;
